import React from "react";

import type { AnnotationMarkerReadModel } from "./annotationMarker.types";

function formatSubtype(value: string | null) {
  if (!value) return null;
  return value
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export default function AnnotationMarkerHoverCard({ marker }: { marker: AnnotationMarkerReadModel }): JSX.Element {
  const sourceLabel = marker.sourceBadgeLabel ?? (marker.annotationSource === "voice" ? "Voice" : marker.annotationSource === "ai" ? "AI" : "Manual");
  const stateLabel = marker.visualState === "published" ? "Published" : "Marker only";
  const subtypeLabel = formatSubtype(marker.subtype);
  const neutralText = "rgba(241, 245, 249, 0.96)";
  const secondaryText = "rgba(226, 232, 240, 0.84)";
  const tertiaryText = "rgba(203, 213, 225, 0.76)";

  return (
    <div
      className="pointer-events-none absolute left-1/2 top-0 z-30 w-64"
      style={{ transform: "translate(calc(-50% + 6px), calc(-100% - 12px))" }}
    >
      <div
        className="overflow-hidden rounded-2xl border border-white/28 bg-white/[0.16] p-3 shadow-[0_34px_96px_rgba(0,0,0,0.34)] dark:bg-black/[0.34]"
        style={{
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
        }}
      >
        <div className="flex items-center gap-2" style={{ color: marker.palette.text }}>
          <span
            className="rounded-full border px-2 py-1"
            style={{
              borderColor: marker.palette.border,
              background: marker.palette.badgeBg,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.08em",
              lineHeight: 1,
            }}
          >
            {marker.shortLabel}
          </span>
          {marker.severity ? (
            <span className="rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em]"
              style={{
                borderColor: "rgba(255,255,255,0.18)",
                background: "rgba(255,255,255,0.12)",
                color: neutralText,
              }}>
              {marker.severity}
            </span>
          ) : null}
          {subtypeLabel ? (
            <span className="rounded-full border px-2 py-1 text-[10px] font-semibold tracking-[0.08em]"
              style={{
                borderColor: "rgba(255,255,255,0.16)",
                background: "rgba(255,255,255,0.1)",
                color: secondaryText,
              }}>
              {subtypeLabel}
            </span>
          ) : null}
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em]" style={{ color: secondaryText }}>{marker.hoverTitle}</span>
        </div>
        <div className="mt-2 text-sm font-semibold" style={{ color: neutralText }}>{marker.summary ?? marker.rawNote}</div>
        {marker.summary && marker.rawNote !== marker.summary ? (
          <div className="mt-1 text-[11px] leading-5" style={{ color: secondaryText }}>{marker.rawNote}</div>
        ) : null}
        {marker.nextActionLabel ? (
          <div className="mt-2 text-[11px] leading-5" style={{ color: secondaryText }}>Next step: {marker.nextActionLabel}</div>
        ) : null}
        <div className="mt-2 text-[11px] leading-5" style={{ color: tertiaryText }}>
          <div>{stateLabel} · {sourceLabel}</div>
          <div>{marker.assetLabel}</div>
          <div>{marker.anchorTimecode ?? `${marker.timeSec.toFixed(2)}s`}</div>
          {marker.frameIndex !== null ? <div>Frame {marker.frameIndex}</div> : null}
          {marker.linkedAudio ? <div>Linked audio anchor</div> : null}
        </div>
      </div>
    </div>
  );
}